import { useEffect, useMemo, useState } from 'react';
import '../styles/dashboard-master.css';

// 既定値を毎レンダー生成しない（新しい [] 参照が templates 依存の useEffect を無限発火させるのを防ぐ）
const EMPTY_ARRAY = [];

function buildFilterOptions(templates, key) {
  const values = new Set();
  templates.forEach((t) => {
    const v = t[key];
    if (v) values.add(v);
  });
  return ['', ...Array.from(values).sort()];
}

export default function TemplateSelectionPopup({ open, templates = EMPTY_ARRAY, phases = EMPTY_ARRAY, onClose, onConfirm, loading }) {
  const [selectedIds, setSelectedIds] = useState(new Set());
  // phaseMap: { templateId: [phaseId, phaseId, ...] }
  const [phaseMap, setPhaseMap] = useState({});
  const [keyword, setKeyword] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [actorFilter, setActorFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');

  useEffect(() => {
    if (open) {
      setSelectedIds(new Set(templates.map((t) => t.id)));
      setPhaseMap({});
      setKeyword('');
      setCategoryFilter('');
      setActorFilter('');
      setDepartmentFilter('');
    }
  }, [open, templates]);

  const phaseById = useMemo(() => {
    const map = {};
    phases.forEach((p) => { map[p.id] = p; });
    return map;
  }, [phases]);

  const addPhase = (tplId, phaseId) => {
    if (!phaseId) return;
    setPhaseMap((prev) => {
      const list = prev[tplId] || [];
      /* istanbul ignore if -- 割当済みフェーズは追加セレクトの候補から外れる */
      if (list.includes(phaseId)) return prev;
      return { ...prev, [tplId]: [...list, phaseId] };
    });
  };

  const removePhase = (tplId, phaseId) => {
    setPhaseMap((prev) => {
      /* istanbul ignore next -- 削除ボタンは割当済みの行にしか出ない */
      const list = (prev[tplId] || []).filter((id) => id !== phaseId);
      const next = { ...prev };
      if (list.length === 0) {
        delete next[tplId];
      } else {
        next[tplId] = list;
      }
      return next;
    });
  };

  const categoryOptions = useMemo(() => buildFilterOptions(templates, 'category_name'), [templates]);
  const actorOptions = useMemo(() => buildFilterOptions(templates, 'actor_name'), [templates]);
  const departmentOptions = useMemo(() => buildFilterOptions(templates, 'department_name'), [templates]);

  const filteredTemplates = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return templates.filter((tpl) => {
      if (categoryFilter && tpl.category_name !== categoryFilter) return false;
      if (actorFilter && tpl.actor_name !== actorFilter) return false;
      if (departmentFilter && tpl.department_name !== departmentFilter) return false;
      if (kw && !(tpl.small_category_name || '').toLowerCase().includes(kw)) return false;
      return true;
    });
  }, [templates, keyword, categoryFilter, actorFilter, departmentFilter]);

  const filteredIds = useMemo(() => new Set(filteredTemplates.map((t) => t.id)), [filteredTemplates]);
  const filteredSelectedCount = useMemo(
    () => filteredTemplates.filter((t) => selectedIds.has(t.id)).length,
    [filteredTemplates, selectedIds],
  );
  const allFilteredSelected = filteredTemplates.length > 0 && filteredSelectedCount === filteredTemplates.length;

  // フェーズ追加分を含めた実際の登録件数
  const totalRecordCount = useMemo(() => {
    let count = 0;
    for (const id of selectedIds) {
      const p = phaseMap[id];
      count += (p && p.length > 0) ? p.length : 1;
    }
    return count;
  }, [selectedIds, phaseMap]);

  if (!open) return null;

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredIds.forEach((id) => next.delete(id));
      } else {
        filteredIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const clearFilters = () => {
    setKeyword('');
    setCategoryFilter('');
    setActorFilter('');
    setDepartmentFilter('');
  };

  const hasFilters = keyword || categoryFilter || actorFilter || departmentFilter;

  return (
    <div className="db-entry-popup-overlay" onClick={onClose} role="presentation">
      <div
        className="db-entry-popup"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="テンプレート選択"
      >
        <div className="db-entry-popup-header">
          <h3>登録するテンプレートを選択</h3>
          <span className="tpl-select-count">{selectedIds.size} / {templates.length} 件選択中（登録: {totalRecordCount} 件）</span>
        </div>
        <div className="tpl-select-toolbar" style={{ flexWrap: 'wrap' }}>
          <label className="tpl-select-toggle">
            <input type="checkbox" checked={allFilteredSelected} onChange={toggleAll} />
            {hasFilters ? `表示中を全選択（${filteredTemplates.length}件）` : '全選択'}
          </label>
          <input
            type="text"
            className="db-input-control"
            placeholder="小項目を検索..."
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            style={{ width: '160px', fontSize: '13px', padding: '6px 10px' }}
          />
          <select
            className="db-select-control"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            style={{ width: 'auto', fontSize: '13px', padding: '6px 8px' }}
          >
            {categoryOptions.map((opt) => (
              <option key={opt || '_all'} value={opt}>{opt || 'カテゴリ: すべて'}</option>
            ))}
          </select>
          <select
            className="db-select-control"
            value={actorFilter}
            onChange={(e) => setActorFilter(e.target.value)}
            style={{ width: 'auto', fontSize: '13px', padding: '6px 8px' }}
          >
            {actorOptions.map((opt) => (
              <option key={opt || '_all'} value={opt}>{opt || '領域: すべて'}</option>
            ))}
          </select>
          <select
            className="db-select-control"
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
            style={{ width: 'auto', fontSize: '13px', padding: '6px 8px' }}
          >
            {departmentOptions.map((opt) => (
              <option key={opt || '_all'} value={opt}>{opt || '設備: すべて'}</option>
            ))}
          </select>
          {hasFilters && (
            <button
              className="master-secondary-button"
              onClick={clearFilters}
              type="button"
              style={{ fontSize: '12px', padding: '5px 10px' }}
            >
              クリア
            </button>
          )}
          {hasFilters && (
            <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: 'auto' }}>
              {filteredTemplates.length} / {templates.length} 件表示
            </span>
          )}
        </div>
        <div className="tpl-select-list">
          <table className="master-preview-table">
            <thead>
              <tr>
                <th style={{ width: '40px' }} />
                <th>カテゴリ</th>
                <th>小項目</th>
                <th>領域</th>
                <th>設備</th>
                <th style={{ minWidth: '180px' }}>フェーズ</th>
              </tr>
            </thead>
            <tbody>
              {filteredTemplates.map((tpl) => {
                const assignedPhases = phaseMap[tpl.id] || [];
                const availablePhases = phases.filter((p) => !assignedPhases.includes(p.id));
                return (
                  <tr key={tpl.id} style={{ cursor: 'pointer' }}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(tpl.id)}
                        onChange={() => toggleOne(tpl.id)}
                      />
                    </td>
                    <td onClick={() => toggleOne(tpl.id)}>{tpl.category_name || '-'}</td>
                    <td onClick={() => toggleOne(tpl.id)}>{tpl.small_category_name || '-'}</td>
                    <td onClick={() => toggleOne(tpl.id)}>{tpl.actor_name || '-'}</td>
                    <td onClick={() => toggleOne(tpl.id)}>{tpl.department_name || '-'}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
                        {assignedPhases.map((pid) => (
                          <span
                            key={pid}
                            className="tpl-phase-tag"
                          >
                            {phaseById[pid]?.phase || pid}
                            <button
                              type="button"
                              className="tpl-phase-tag-remove"
                              onClick={() => removePhase(tpl.id, pid)}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                        {availablePhases.length > 0 && (
                          <select
                            className="db-select-control"
                            value=""
                            onChange={(e) => addPhase(tpl.id, Number(e.target.value))}
                            style={{ width: 'auto', fontSize: '11px', padding: '2px 4px', minWidth: '40px' }}
                          >
                            <option value="">+</option>
                            {availablePhases.map((p) => (
                              <option key={p.id} value={p.id}>{p.phase}</option>
                            ))}
                          </select>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filteredTemplates.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: '20px', color: '#9ca3af' }}>
                    該当するテンプレートがありません
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="tpl-select-footer">
          <button className="master-secondary-button" onClick={onClose} type="button" disabled={loading}>
            キャンセル
          </button>
          <button
            className="master-primary-button"
            onClick={() => onConfirm([...selectedIds], phaseMap)}
            disabled={selectedIds.size === 0 || loading}
            type="button"
          >
            {loading ? '登録中...' : `${totalRecordCount} 件を登録`}
          </button>
        </div>
      </div>
    </div>
  );
}
